import { SmartSortProduct } from '@iheartjane/dm-sdk';
import type { CompareProduct } from '@iheartjane/menu';
import { useCompareActions } from '@iheartjane/menu';
import { useToast } from '@iheartjane/reefer';
import { EventNames } from '@iheartjane/tracking';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import type { DeepReadonly } from 'ts-essentials';

import { isCompareProductPayload } from '@jane/shared-ecomm/components';
import type { AttemptToAddToCartProps } from '@jane/shared-ecomm/providers';
import { track } from '@jane/shared-ecomm/tracking';
import { useJaneUser } from '@jane/shared/data-access';
/* eslint-disable-next-line no-restricted-imports */
import type { Store } from '@jane/shared/models';

interface CompareHandlersRegistrarProps {
  attemptToAddToCart: (props: AttemptToAddToCartProps) => void;
  store: DeepReadonly<Store>;
}

/**
 * Renders nothing. Registers the compare modal's add-to-bag and product-click
 * handlers with the provider.
 */
export const CompareHandlersRegistrar = ({
  attemptToAddToCart,
  store,
}: CompareHandlersRegistrarProps) => {
  const { data: janeUser } = useJaneUser();
  const { closeModal, setHandlers } = useCompareActions();
  const toast = useToast();
  const navigate = useNavigate();

  const trackingProps = useCallback(
    (product: CompareProduct) => ({
      productBrand: product.brand,
      productCategory: product.category,
      productId: product.id,
      productKind: product.kind,
      productName: product.name,
      storeId: String(store.id),
      storeName: store.name,
      url: window.location.href,
      userType: janeUser?.user?.authenticated
        ? ('registered' as const)
        : ('guest' as const),
    }),
    [janeUser?.user?.authenticated, store.id, store.name]
  );

  /** Credits the placement that originally surfaced this product, if any. */
  const trackDmClick = useCallback(
    (smartProductJson: string | null, isAddToCart: boolean) => {
      if (!smartProductJson) return;
      try {
        SmartSortProduct.fromJSON(smartProductJson)
          .click({ isAddToCart })
          .catch(() => {});
      } catch {
        // A malformed serialised instance should never block the add-to-bag.
      }
    },
    []
  );

  const addToCart = useCallback(
    (product: CompareProduct) => {
      if (!isCompareProductPayload(product.payload)) {
        if (product.url) {
          closeModal();
          navigate(product.url);
          return;
        }
        toast.add({
          label: 'Sorry, we could not add that to your bag.',
          time: 3000,
        });
        return;
      }
      const { menuProduct, priceId, smartProductJson } = product.payload;

      attemptToAddToCart({
        count: 1,
        location: 'menu',
        menuProduct,
        price_id: priceId,
        store,
      });
      trackDmClick(smartProductJson, true);
      track({
        event: EventNames.AddedCompareModalProductToCart,
        priceUnit: priceId,
        ...trackingProps(product),
      });
    },
    [
      attemptToAddToCart,
      closeModal,
      navigate,
      store,
      toast,
      trackDmClick,
      trackingProps,
    ]
  );

  // Navigation is handled by the modal's links — this only tracks the click.
  const view = useCallback(
    (product: CompareProduct) => {
      if (isCompareProductPayload(product.payload)) {
        trackDmClick(product.payload.smartProductJson, false);
      }
      track({
        event: EventNames.ClickedCompareModalProduct,
        ...trackingProps(product),
      });
    },
    [trackDmClick, trackingProps]
  );

  useEffect(() => {
    setHandlers({ addToCart, view });
    return () => setHandlers({});
  }, [setHandlers, addToCart, view]);

  return null;
};
